/**
 * Browser input -> RustDesk input events, mirroring the desktop client.
 *
 * Keyboard uses **Map mode**, which is what the desktop and web clients use by
 * default. The identity of a key travels positionally, and the *controlling*
 * side converts it into the code the *peer's* platform expects:
 *
 *     KeyboardEvent.code -> Key -> peer-platform code -> KeyEvent.chr
 *
 * The tables come straight from rdev (see keycodes.ts), so this produces the
 * same bytes the desktop client would for the same physical key. Sending a
 * Windows scancode to a Linux peer yields the wrong key entirely, which is why
 * the peer's platform selects the table.
 *
 * In Map mode modifiers are NOT sent as a `modifiers` array: Shift, Control and
 * friends are ordinary physical keys that generate their own down/up events,
 * and the peer tracks their state. Sending both would double-apply them.
 */
import { KeyboardMode, ControlKey } from "./message.js";
import { CODE_TO_KEY, KEY_TO_WIN_SCANCODE, KEY_TO_LINUX, KEY_TO_MACOS, } from "./keycodes.js";
export function peerPlatformOf(platform) {
    const p = (platform ?? "").toLowerCase();
    if (p.includes("windows"))
        return "windows";
    if (p.includes("mac") || p.includes("darwin"))
        return "macos";
    if (p.includes("android"))
        return "android";
    if (p.includes("linux") || p.includes("ubuntu") || p.includes("debian"))
        return "linux";
    return "unknown";
}
/** Translates a DOM key code into the positional code the peer expects. */
export function positionCodeFor(domCode, peer) {
    const key = CODE_TO_KEY[domCode];
    if (!key)
        return null;
    switch (peer) {
        case "windows": {
            const v = KEY_TO_WIN_SCANCODE[key];
            return v === undefined ? null : v;
        }
        case "linux":
        case "android": {
            const v = KEY_TO_LINUX[key];
            return v === undefined ? null : v;
        }
        case "macos": {
            const v = KEY_TO_MACOS[key];
            return v === undefined ? null : v;
        }
        default: {
            // Unknown peer: Windows scancodes are the most widely accepted.
            const v = KEY_TO_WIN_SCANCODE[key];
            return v === undefined ? null : v;
        }
    }
}
/**
 * Builds the KeyEvent for a browser keyboard event.
 * Returns null for keys with no positional mapping, so the caller can fall
 * back to sending the character.
 */
export function keyEventFrom(e, down, peer) {
    return keyEventFor({ code: e.code, key: e.key }, down, peer);
}
/**
 * The same, for a key described rather than delivered.
 *
 * A screen shown in its own window reports what was pressed to the window
 * that holds the connection, which never saw the event itself.
 */
export function keyEventFor(e, down, peer) {
    const code = positionCodeFor(e.code, peer);
    if (code !== null) {
        return { down, chr: code, mode: KeyboardMode.Map };
    }
    // No positional mapping (an exotic or synthetic key). Send the character
    // itself on key-down so typing still works.
    if (down && e.key && e.key.length === 1) {
        return { down: true, press: true, unicode: e.key.codePointAt(0), mode: KeyboardMode.Legacy };
    }
    return null;
}
/**
 * Mouse event mask, from `input` in the client's common.rs.
 *
 *     mask = (button << 3) | type
 *
 * `button` is a BIT FLAG, not an index: left is 0x01, right 0x02, middle 0x04.
 * Using an index puts no button bit in the mask at all, and the peer then
 * treats a click as a press of nothing.
 */
export const MouseType = {
    Move: 0,
    Down: 1,
    Up: 2,
    Wheel: 3,
    Trackpad: 4,
};
export const MouseButton = {
    Left: 0x01,
    Right: 0x02,
    Middle: 0x04,
    Back: 0x08,
    Forward: 0x10,
};
export const mouseMask = (button, type) => (button << 3) | type;
/** DOM MouseEvent.button -> RustDesk button flag. */
const DOM_BUTTON = {
    0: MouseButton.Left,
    1: MouseButton.Middle,
    2: MouseButton.Right,
    3: MouseButton.Back,
    4: MouseButton.Forward,
};
/**
 * Maps a pointer position on the canvas to remote screen coordinates.
 *
 * The canvas is letterboxed by `object-fit: contain`, so the drawn image does
 * not necessarily fill the element. Mapping against the element's own box
 * would skew every coordinate whenever the aspect ratios differ.
 */
export function toRemoteCoords(clientX, clientY, rect, view) {
    if (!rect.width || !rect.height || !view.remoteWidth || !view.remoteHeight) {
        return { x: view.remoteX, y: view.remoteY };
    }
    const scale = Math.min(rect.width / view.remoteWidth, rect.height / view.remoteHeight);
    const drawnW = view.remoteWidth * scale;
    const drawnH = view.remoteHeight * scale;
    const offX = (rect.width - drawnW) / 2;
    const offY = (rect.height - drawnH) / 2;
    const x = (clientX - rect.left - offX) / scale;
    const y = (clientY - rect.top - offY) / scale;
    // Clamp: a pointer in the letterbox margin would otherwise land off-screen.
    return {
        x: Math.round(view.remoteX + Math.max(0, Math.min(view.remoteWidth - 1, x))),
        y: Math.round(view.remoteY + Math.max(0, Math.min(view.remoteHeight - 1, y))),
    };
}
export function mouseMoveEvent(coords) {
    return { mask: MouseType.Move, x: coords.x, y: coords.y };
}
export function mouseButtonEvent(e, down, coords) {
    return mouseButtonEventFor(e.button, down, coords);
}
/** The same, for a button number rather than an event. */
export function mouseButtonEventFor(domButton, down, coords) {
    const button = DOM_BUTTON[domButton];
    if (button === undefined)
        return null;
    return {
        mask: mouseMask(button, down ? MouseType.Down : MouseType.Up),
        x: coords.x,
        y: coords.y,
    };
}
/**
 * Wheel events.
 *
 * With mask = Wheel the coordinate fields carry scroll *deltas*, not a
 * position. The desktop client sends notches; browsers usually report pixels
 * (deltaMode 0), so those are converted. The sign is inverted because the
 * protocol's positive Y is "scroll up" while the DOM's is "content moves down".
 */
export function wheelEvent(e) {
    return wheelEventFor(e.deltaX, e.deltaY, e.deltaMode === 0);
}
/** The same, for deltas rather than an event. */
export function wheelEventFor(deltaX, deltaY, byPixel) {
    const PIXELS_PER_NOTCH = 100;
    const toNotches = (delta) => {
        if (delta === 0)
            return 0;
        const n = byPixel ? delta / PIXELS_PER_NOTCH : delta;
        const rounded = Math.trunc(n);
        // Never swallow a small scroll: keep at least one notch of movement.
        return rounded !== 0 ? -rounded : delta > 0 ? -1 : 1;
    };
    return { mask: MouseType.Wheel, x: toNotches(deltaX), y: toNotches(deltaY) };
}
/** Ctrl+Alt+Del, which a browser will never deliver as a real key event. */
export function ctrlAltDelEvent() {
    return { down: true, press: true, controlKey: ControlKey.CtrlAltDel };
}
export function lockScreenEvent() {
    return { down: true, press: true, controlKey: ControlKey.LockScreen };
}
